import {
  type DlCustomLocale,
  PatientLookupIdType,
  ScannerFlow,
  SpecialistAssignmentStrategy,
} from '@dermloop/static-values';
import { Exclude } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsTimeZone,
  IsUUID,
  Min,
  ValidateIf,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from './base.dto';
import { FederationDto } from './federation.dtos';

export interface JurisdictionalGroupDto extends BaseDto {
  name: string;
  allowedEmailDomainRegex?: string;
  allowedEmailDomains?: string[];
  selfReactivationEnabled: boolean;
  trustedDomainMfaSkipEnabled?: boolean;
  fax?: string;
  description?: string;
  sendFaxOnReferralClosed?: boolean;
  scannerFlow: ScannerFlow;
  federation?: FederationDto;
  federationId?: string;
  /** @deprecated Read `federation.locale` */
  locale?: DlCustomLocale;
  defaultPreferredTeleSpecialistsId?: string;
  defaultAllowedTeleSpecialistsId?: string;
  patientLookupIdType?: PatientLookupIdType;
  payerOptions?: string[];
  fhirReference?: string;
  pdfDownloadEnabled?: boolean;
  displayEligiblePayers?: boolean;
  hl7MessagesEnabled?: boolean;
  ehrManagedActionsEnabled?: boolean;
  oidcConfigurationUrl?: string;
  /** IANA time zone; `null`/absent means UTC (the pod timezone). */
  timeZone?: string | null;
}

export class CreateJurisdictionalGroupDto
  extends BaseCreateDto
  implements Partial<JurisdictionalGroupDto>
{
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  allowedEmailDomains?: string[];

  @IsOptional()
  @IsBoolean()
  selfReactivationEnabled?: boolean;

  // The column is NOT NULL, so an explicit null must fail validation, not the write.
  @ValidateIf((_, value) => value !== undefined)
  @IsBoolean()
  trustedDomainMfaSkipEnabled?: boolean;

  @IsOptional()
  @IsString()
  fax?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsBoolean()
  sendFaxOnReferralClosed?: boolean;

  @IsEnum(ScannerFlow)
  scannerFlow: ScannerFlow;

  @IsOptional()
  @IsUUID()
  federationId?: string;

  @Exclude()
  federation?: never;

  @IsOptional()
  @IsUUID()
  defaultPreferredTeleSpecialistsId?: string;

  @IsOptional()
  @IsUUID()
  defaultAllowedTeleSpecialistsId?: string;

  @IsOptional()
  @IsEnum(PatientLookupIdType)
  patientLookupIdType?: PatientLookupIdType;

  @IsOptional()
  @IsString({ each: true })
  payerOptions?: string[];

  @IsOptional()
  @IsString()
  fhirReference?: string;

  @IsOptional()
  @IsBoolean()
  pdfDownloadEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  displayEligiblePayers?: boolean;

  @IsOptional()
  @IsBoolean()
  hl7MessagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  ehrManagedActionsEnabled?: boolean;

  @IsOptional()
  @IsString()
  oidcConfigurationUrl?: string;

  @IsOptional()
  @IsTimeZone({
    message:
      "timeZone must be a valid IANA time zone identifier (e.g. 'Europe/Copenhagen')",
  })
  timeZone?: string;

  @IsOptional()
  @IsEnum(SpecialistAssignmentStrategy)
  specialistAssignmentStrategy?: SpecialistAssignmentStrategy;
}

export class UpdateJurisdictionalGroupDto
  extends BaseUpdateDto
  implements Partial<JurisdictionalGroupDto>
{
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  name?: string;

  @IsOptional()
  @IsString()
  allowedEmailDomainRegex?: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  allowedEmailDomains?: string[];

  @IsOptional()
  @IsBoolean()
  selfReactivationEnabled?: boolean;

  // The column is NOT NULL, so an explicit null must fail validation, not the write.
  @ValidateIf((_, value) => value !== undefined)
  @IsBoolean()
  trustedDomainMfaSkipEnabled?: boolean;

  @IsOptional()
  @IsString()
  fax?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsBoolean()
  sendFaxOnReferralClosed?: boolean;

  @IsOptional()
  @IsEnum(ScannerFlow)
  scannerFlow?: ScannerFlow;

  @IsOptional()
  @IsUUID()
  federationId?: string;

  @Exclude()
  federation?: never;

  /** If set to null, will remove id from entity */
  @IsOptional()
  @IsUUID()
  defaultPreferredTeleSpecialistsId?: string;

  /** If set to null, will remove id from entity */
  @IsOptional()
  @IsUUID()
  defaultAllowedTeleSpecialistsId?: string;

  @IsOptional()
  @IsEnum(PatientLookupIdType)
  patientLookupIdType?: PatientLookupIdType;

  @IsOptional()
  @IsString({ each: true })
  payerOptions?: string[];

  @IsOptional()
  @IsString()
  fhirReference?: string;

  @IsOptional()
  @IsBoolean()
  pdfDownloadEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  displayEligiblePayers?: boolean;

  @IsOptional()
  @IsBoolean()
  hl7MessagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  ehrManagedActionsEnabled?: boolean;

  @IsOptional()
  @IsString()
  oidcConfigurationUrl?: string;

  /**
   * Explicit `null` clears the time zone (falls back to UTC); omitting the
   * field leaves the stored value unchanged.
   */
  @IsOptional()
  @IsTimeZone({
    message:
      "timeZone must be a valid IANA time zone identifier (e.g. 'Europe/Copenhagen')",
  })
  timeZone?: string | null;
}

export interface JurisdictionalGroupStatsDto {
  siteCount: number;
  userCount: number;
}

/**
 * DTO for querying jurisdictional groups.
 */
export class JurisdictionalGroupSearchQueryDto {
  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  take?: number;

  @IsOptional()
  @IsString()
  searchText?: string;

  @IsOptional()
  @IsUUID()
  federationId?: string;

  @IsOptional()
  @IsEnum(ScannerFlow, { each: true })
  scannerFlows?: ScannerFlow[];
}
